import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { SERVER_URL } from "../api";
import { getGroupById } from "../api/group";
import profileContainer from "../assets/results/circulo-perfil.svg";
import { setGame, setGroup } from "../redux/actions";
import { ROUTES } from "../utils/constants";
import { IconRefresh } from "@tabler/icons-react";
import { clearTabletLocalSession, getGroupId, updateStats, formatGameScore } from "../utils/helpers";
import "./StartGame.css";
import "./SessionEndPages.css";

const getLevelInfo = (scoreRaw) => {
	const s = Number.isFinite(scoreRaw) ? scoreRaw : 0;

	if (s >= 130) {
		return {
			key: "master",
			title: "Nivel Master",
			desc: "Dominan el caso clínico a la perfección.",
			tone: "success",
		};
	}
	if (s >= 100 && s <= 129) {
		return {
			key: "experto",
			title: "Nivel Experto",
			desc: "Dominan el caso clínico, resolvieron casi todo con precisión.",
			tone: "success",
		};
	}
	if (s >= 80 && s <= 99) {
		return {
			key: "muy-buen-desempeno",
			title: "Muy buen desempeño",
			desc: "Demuestran buen criterio clínico, con algunos detalles por mejorar.",
			tone: "good",
		};
	}
	if (s >= 50 && s <= 79) {
		return {
			key: "competente",
			title: "Competente",
			desc: "Lograron avanzar, aunque con varios errores; aún hay áreas por reforzar.",
			tone: "neutral",
		};
	}
	if (s >= 10 && s <= 49) {
		return {
			key: "en-proceso",
			title: "En proceso",
			desc: "Resolvieron lo básico, pero requieren mayor análisis clínico.",
			tone: "warn",
		};
	}
	if (s >= 0 && s <= 9) {
		return {
			key: "minimo",
			title: "Mínimo alcanzado",
			desc: "Apenas superaron el reto, es necesario repasar los fundamentos.",
			tone: "alert",
		};
	}
	return {
		key: "retry",
		title: "Debes intentarlo de nuevo",
		desc: "El desempeño no fue suficiente, se recomienda repetir la experiencia.",
		tone: "retry",
	};
};

function Results() {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const [group, setCurrentGroup] = useState(useSelector((state) => state.group));
	const [img, setImg] = useState(null);
	const [time, setTime] = useState(group?.time || 0);
	const [groupName, setGroupName] = useState(null);

	const level = getLevelInfo(group?.score ?? 0);

	const handleStealthResetSession = (e) => {
		e.preventDefault();
		e.stopPropagation();

		// Limpieza completa de sesión de juego (localStorage + cookies de sesión del navegador)
		try {
			const gid = group?._id || getGroupId();
			if (gid) localStorage.removeItem(`specialistHelp:${gid}`);
			Object.keys(localStorage)
				.filter((k) => k.startsWith("specialistHelp:"))
				.forEach((k) => localStorage.removeItem(k));
		} catch {}

		try {
			document.cookie
				.split(";")
				.map((c) => c.trim())
				.filter(Boolean)
				.forEach((cookie) => {
					const eqPos = cookie.indexOf("=");
					const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;
					document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
				});
		} catch {}

		clearTabletLocalSession();
		dispatch(setGroup(null));
		dispatch(setGame(null));
		navigate(ROUTES.HOME, { replace: true });
	};

	useEffect(() => {
		if (group) {
			getGroupById(group?._id)
				.then((res) => {
					if (res.status === 200) {
						const fresh = res.data;

						const reduxTime = Number(group?.time) || 0;
						const serverTime = Number(fresh?.time) || 0;
						const bestTime = Math.max(reduxTime, serverTime);

						setCurrentGroup({ ...fresh, time: bestTime });
						setGroupName(fresh?.fullName || null);
						setImg(fresh?.image ? `${SERVER_URL}/${fresh.image}` : null);
						setTime((prev) => Math.max(prev, serverTime));

						const merged = {
							...fresh,
							time: bestTime,
							lastVisited: ROUTES.RESULTS,
							finished: true,
						};
						dispatch(setGroup(merged));
						updateStats(merged);
					}
				})
				.catch(() => {});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- una sola sincronización al entrar en resultados
	}, []);

	const mm = Math.floor(time / 60)
		.toString()
		.padStart(2, "0");
	const ss = (time % 60).toString().padStart(2, "0");

	return (
		<div className="start-game-page session-end-page session-end-page--results">
			<div className="start-game-inner start-game-inner--prep5 session-end-inner position-relative">
				<span className="start-game-badge sg5-badge">
					<span className="start-game-badge-dot" aria-hidden />
					Resultados
				</span>

				<div className="sg5-scroll session-results-scroll">
					<header className="sg5-hero session-results-hero">
						<h1 className="sg5-title">
							Resultado del <span className="sg5-title-accent">equipo {group?.tableNum || "—"}</span>
						</h1>
						<p className="sg5-lead session-end-lead">
							Resumen de la partida: tiempo total, puntos y valoración según el rendimiento alcanzado.
						</p>
					</header>

					<div className="session-results-grid">
						<div className="session-results-panel session-results-panel--media">
							<div className="session-results-ring-wrap">
								<img src={profileContainer} alt="" className="session-results-ring-svg" />
								<div className="session-results-teamfoto">
									{img ? <img src={img} alt="Foto del equipo" /> : null}
								</div>
							</div>
							<div className="session-results-teamname">{groupName || "Nombre equipo"}</div>
						</div>

						<div className="session-results-panel">
							<div className="session-results-stat">
								<div className="session-results-stat-label">Tiempo de juego</div>
								<div className="session-results-timers" aria-label={`${mm} minutos ${ss} segundos`}>
									<div className="session-results-digit">{mm}</div>
									<span className="session-results-digit-sep">:</span>
									<div className="session-results-digit">{ss}</div>
								</div>
							</div>

							<div className="session-results-stat">
								<div className="session-results-stat-label">Puntos obtenidos</div>
								<div className="session-results-score-box">{formatGameScore(group?.score ?? 0)}</div>
							</div>

							<div className="session-results-level">
								<div className="session-results-level-head">
									<span className="session-results-level-tag">Nivel alcanzado</span>
									<span className="session-results-level-pill">{level.title}</span>
								</div>
								<p className="session-results-level-desc">{level.desc}</p>
							</div>
						</div>
					</div>
				</div>

				<button
					type="button"
					onClick={handleStealthResetSession}
					title="Reiniciar juego"
					aria-label="Reiniciar juego"
					className="session-results-restart"
				>
					<IconRefresh size={18} stroke={2} aria-hidden />
					<span>Reiniciar juego</span>
				</button>
			</div>
		</div>
	);
}

export default Results;
